import type { ConsentSignatureRow, ConsentVersionRow, DriverProfileRow, HandbookAckRow, HandbookVersionRow } from '@routeback/core';
import { useToast } from '@routeback/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../../lib/app';
import { useUniformItems } from '../uniforms/useUniformData';
import { badPhone, formatPhone, saveMyProfile } from './profile';

function useMyProfileRow(): DriverProfileRow | null | undefined {
  const app = useApp();
  return useLiveQuery(
    async () => (await app.db.table<DriverProfileRow, string>('driver_profiles').where('company_id').equals(app.company.id).toArray())
      .find((p) => !p.deleted_at && p.user_id === app.userId) ?? null,
    [app.db, app.company.id, app.userId],
  );
}

/** My phone and emergency contact — managers see these on the People screen. */
export function ContactCard() {
  const app = useApp();
  const toast = useToast();
  const profile = useMyProfileRow();
  const [f, setF] = useState({ phone: '', emergency_name: '', emergency_phone: '' });
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (profile === undefined || loaded) return;
    setF({ phone: profile?.phone ?? '', emergency_name: profile?.emergency_name ?? '', emergency_phone: profile?.emergency_phone ?? '' });
    setLoaded(true);
  }, [profile, loaded]);
  const dirty = !!profile ? f.phone !== profile.phone || f.emergency_name !== profile.emergency_name || f.emergency_phone !== profile.emergency_phone : !!(f.phone || f.emergency_name || f.emergency_phone);
  const bad = badPhone(f.phone) || badPhone(f.emergency_phone);
  const save = async () => {
    const next = { phone: formatPhone(f.phone), emergency_name: f.emergency_name.trim(), emergency_phone: formatPhone(f.emergency_phone) };
    await saveMyProfile(app, next);
    setF(next);
    toast('Saved');
  };
  const missing = loaded && !f.emergency_phone.trim();
  return (
    <>
      <div className="section-title">Contact info</div>
      <div className="card">
        {missing && <p className="small" style={{ marginTop: 0, color: 'var(--warn)' }}>Add someone we can call if something happens to you on the road.</p>}
        <div className="field"><label htmlFor="me-phone">My phone</label>
          <input id="me-phone" type="tel" inputMode="tel" autoComplete="tel" value={f.phone} placeholder="630-555-0100" onChange={(e) => setF({ ...f, phone: e.target.value })} />
        </div>
        <div className="ar-sub">Emergency contact</div>
        <div className="field"><label htmlFor="me-ec-name">Name</label>
          <input id="me-ec-name" value={f.emergency_name} placeholder="e.g. Jordan Smith (spouse)" onChange={(e) => setF({ ...f, emergency_name: e.target.value })} />
        </div>
        <div className="field"><label htmlFor="me-ec-phone">Phone</label>
          <input id="me-ec-phone" type="tel" inputMode="tel" value={f.emergency_phone} onChange={(e) => setF({ ...f, emergency_phone: e.target.value })} />
        </div>
        {bad && <p className="small" style={{ color: 'var(--danger)', marginTop: -6 }}>Check the phone number — it should have 10 digits.</p>}
        <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
          <span className="muted small">Only you and your managers can see these.</span>
          <button className="btn btn-primary" disabled={!dirty || bad || !app.active} onClick={() => void save()}>Save</button>
        </div>
      </div>
    </>
  );
}

/** My uniform sizes, without starting a request. */
export function SizesCard() {
  const app = useApp();
  const toast = useToast();
  const items = useUniformItems();
  const profile = useMyProfileRow();
  const [sizes, setSizes] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (profile === undefined || loaded) return;
    setSizes(profile?.sizes ?? {});
    setLoaded(true);
  }, [profile, loaded]);
  if (!items.length) return null;
  const saved = profile?.sizes ?? {};
  const dirty = items.some((i) => (sizes[i.id] ?? '') !== (saved[i.id] ?? ''));
  const save = async () => {
    await saveMyProfile(app, { sizes: Object.fromEntries(Object.entries(sizes).filter(([, v]) => v.trim()).map(([k, v]) => [k, v.trim()])) });
    toast('Sizes saved');
  };
  return (
    <>
      <div className="section-title">My uniform sizes</div>
      <div className="card">
        {items.map((it) => (
          <div key={it.id} className="row" style={{ justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
            <span className="grow">{it.name}</span>
            {it.sizes.length ? (
              <select aria-label={`${it.name} size`} value={sizes[it.id] ?? ''} onChange={(e) => setSizes({ ...sizes, [it.id]: e.target.value })} style={{ width: 132 }}>
                <option value="">—</option>
                {it.sizes.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            ) : (
              <input aria-label={`${it.name} size`} value={sizes[it.id] ?? ''} placeholder={it.size_hint || 'Size'} onChange={(e) => setSizes({ ...sizes, [it.id]: e.target.value })} style={{ width: 132 }} />
            )}
          </div>
        ))}
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <Link to="/uniforms" className="btn btn-sm btn-ghost">Request uniforms ›</Link>
          <button className="btn btn-primary" disabled={!dirty || !app.active} onClick={() => void save()}>Save sizes</button>
        </div>
      </div>
    </>
  );
}

/** What I've signed (handbook, consents) — version and date. */
export function SignaturesCard() {
  const app = useApp();
  const rows = useLiveQuery(async () => {
    const t = <T,>(n: string) => app.db.table<T, string>(n).where('company_id').equals(app.company.id).toArray();
    const [hv, ha, cv, cs] = await Promise.all([t<HandbookVersionRow>('handbook_versions'), t<HandbookAckRow>('handbook_acks'), t<ConsentVersionRow>('consent_versions'), t<ConsentSignatureRow>('consent_signatures')]);
    const out = [
      ...ha.filter((a) => a.user_id === app.userId && !a.deleted_at).map((a) => {
        const v = hv.find((x) => x.id === a.handbook_version_id);
        return { id: a.id, what: v?.title || 'Employee handbook', version: v?.version ?? '', current: !!v?.active, name: a.typed_name, at: a.signed_at };
      }),
      ...cs.filter((s) => s.user_id === app.userId && !s.deleted_at).map((s) => {
        const v = cv.find((x) => x.id === s.consent_version_id);
        return { id: s.id, what: v?.title || 'Consent', version: v?.version ?? '', current: !!v?.active, name: s.typed_name, at: s.signed_at };
      }),
    ];
    return out.sort((a, b) => b.at.localeCompare(a.at));
  }, [app.db, app.company.id, app.userId], []);
  if (!rows.length) return null;
  return (
    <>
      <div className="section-title">What I've signed</div>
      <div className="list">
        {rows.map((r) => (
          <div key={r.id} className="list-item">
            <div className="grow">
              <div>{r.what}{r.version ? <span className="muted"> · {r.version}</span> : null}</div>
              <div className="muted small">Signed “{r.name}” · {new Date(r.at).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
            </div>
            {r.current ? <span className="chip on">Current</span> : <span className="chip">Older version</span>}
          </div>
        ))}
      </div>
    </>
  );
}
